import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { ThemeName, themes, projectEssentialThemes } from './themeConfig';

export type ThemeModule =
  | 'super-picker'
  | 'super-calculator'
  | 'super-connector'
  | 'project-deck'
  | 'project-essential'
  | 'super-personalized-psf'
  | 'communication'
  | 'data-dashboard'
  | 'app';

interface ThemeContextType {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  module: ThemeModule;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function getModuleFromPath(pathname: string): ThemeModule {
  if (pathname.startsWith('/super-picker')) return 'super-picker';
  if (
    pathname.startsWith('/super-calculator') ||
    pathname.startsWith('/purchase-planner') ||
    pathname.startsWith('/mortgage-loan') ||
    pathname.startsWith('/profile-manager')
  ) {
    return 'super-calculator';
  }
  if (pathname.startsWith('/super-connector')) return 'super-connector';
  if (pathname.startsWith('/project-deck')) return 'project-deck';
  if (pathname.startsWith('/project-essential')) return 'project-essential';
  if (pathname.startsWith('/super-personalized-psf')) return 'super-personalized-psf';
  if (pathname.startsWith('/communication')) return 'communication';
  if (pathname.startsWith('/data-dashboard')) return 'data-dashboard';
  return 'app';
}

function getStorageKey(module: ThemeModule): string {
  return module === 'app' ? 'app-theme' : `app-theme-${module}`;
}

function getDefaultTheme(module: ThemeModule): ThemeName {
  switch (module) {
    case 'super-picker':
      return 'dark';
    case 'super-calculator':
      return 'light';
    case 'super-connector':
      return 'dark';
    case 'project-deck':
      return 'dark';
    case 'project-essential':
      return 'classic';
    case 'super-personalized-psf':
      return 'dark';
    case 'communication':
      return 'dark';
    case 'data-dashboard':
      return 'dark';
    default:
      return 'dark';
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const location = useLocation();
  const module = useMemo(() => getModuleFromPath(location.pathname), [location.pathname]);
  const storageKey = getStorageKey(module);

  const themeFromQuery = useMemo((): ThemeName | null => {
    const raw = new URLSearchParams(location.search).get('theme') as ThemeName | null;
    return raw && themes[raw] ? raw : null;
  }, [location.search]);

  const [currentTheme, setCurrentThemeState] = useState<ThemeName>(() => {
    if (themeFromQuery) return themeFromQuery;
    const savedTheme = localStorage.getItem(storageKey) as ThemeName;
    return savedTheme && themes[savedTheme] ? savedTheme : getDefaultTheme(module);
  });

  // When module changes, load theme for that module. A one-shot ?theme=
  // override (PE embedded in Super Picker) wins on that navigation only;
  // later in-app PE routes keep the in-memory theme via postMessage / state.
  useEffect(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('theme') as ThemeName | null;
    if (fromQuery && themes[fromQuery]) {
      setCurrentThemeState(fromQuery);
      return;
    }
    const savedTheme = localStorage.getItem(storageKey) as ThemeName;
    setCurrentThemeState(savedTheme && themes[savedTheme] ? savedTheme : getDefaultTheme(module));
  }, [module, storageKey]);

  // Apply ?theme= when present (initial embed load). Do not revert when the
  // query is dropped during client-side navigation inside the iframe.
  useEffect(() => {
    if (themeFromQuery) {
      setCurrentThemeState(themeFromQuery);
    }
  }, [themeFromQuery]);

  const setTheme = (theme: ThemeName) => {
    setCurrentThemeState(theme);
    localStorage.setItem(storageKey, theme);
  };

  // Live theme sync from parent Super Picker iframe host. Does not persist —
  // standalone PE visits keep their own stored preference.
  useEffect(() => {
    if (module !== 'project-essential') return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== 'transformation-set-project-essential-theme') return;
      const next = data.theme as ThemeName;
      if (next && themes[next]) {
        setCurrentThemeState(next);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [module]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  // Expose the active module on <html> so CSS can scope rules to a module
  // independently of the active theme (e.g. apply Avenir + brand-navy
  // accents to all three Project Essentials theme variants).
  useEffect(() => {
    document.documentElement.setAttribute('data-module', module);
  }, [module]);

  // Update document title based on module
  useEffect(() => {
    document.title =
      module === 'super-calculator' ? 'SUPER CALCULATOR'
      : module === 'super-connector' ? 'SUPER CONNECTOR'
      : module === 'project-deck' ? 'PROJECT DECK'
      : module === 'project-essential' ? 'PROJECT ESSENTIALS'
      : module === 'communication' ? 'COMMUNICATION'
      : module === 'super-personalized-psf' ? 'SUPER PERSONALIZED PSF'
      : module === 'data-dashboard' ? 'DATA DASHBOARD'
      : 'SUPER PICKER';
  }, [module]);

  // Project Essentials resolves to its own theme map so the brand accent
  // and Avenir typography stay applied across all three theme variants.
  const themeConfig =
    module === 'project-essential'
      ? projectEssentialThemes[currentTheme]
      : themes[currentTheme];
  const contextValue = useMemo(
    () => ({ currentTheme, setTheme, module }),
    [currentTheme, module]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={themeConfig.theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
